import { isEqual as _isEqual, isArray } from 'lodash-es';
import { create } from 'zustand';
import { Channel, FILTER_CONFIG, FilterType, FilterValue, UnionInteractionsByHcp } from '@/models';
import { getActiveFilters } from '@/utils/filter';

import { ActiveFilter, FilterState } from '@/types/filter';
import { Page } from '@/types/cube';
import { cleanFilterState } from '../components/EvolutionDiagram/utils/cleanFilterState';
import { SESSION_STORAGE_SYNC_EVENT, SessionStorageKey } from '@/hooks/useSessionStorage';
import {
  getSecondarySegmentFiltersForChannel,
  readSecondarySegmentFilters,
  writeSecondarySegmentFilters,
} from './secondarySegmentFilterStorage';

export const getDefaultFiltersState = (customFilterConfig?: FilterType[]): FilterState =>
  (customFilterConfig ?? []).reduce(
    (acc, key) => {
      const config = FILTER_CONFIG[key as FilterType];
      acc[key as FilterType] = config.defaultValue;
      return acc;
    },
    {} as Record<FilterType, FilterValue>,
  );

export type Mode = 'hcp' | 'hco';

export type SortConfig = {
  field: string;
  desc?: boolean;
};

export type TableState = {
  pagination: Page;
  sorts: SortConfig[];
};

interface ChannelFocusState {
  channel: Channel;
  interactionFilter: FilterState;
  timeRangeInMonth: number;
  mode: Mode;
  filterKeys: FilterType[];
  currentHCO: UnionInteractionsByHcp | null;
  tables: Record<string, TableState>;
}

interface ChannelFocusActions {
  setChannel: (channel: Channel) => void;
  setInteractionFilter: (filter: FilterState) => void;
  clearFilters: () => void;
  clearSingleFilter: (filterKey: FilterType, value: FilterValue) => void;
  isFilterActive: () => boolean;
  setTimeRangeInMonth: (timeRangeInMonth: number) => void;
  getActiveFilters: () => ActiveFilter[];
  setMode: (mode: Mode) => void;
  setFilterKeys: (keys: FilterType[]) => void;
  setCurrentHCO: (currentHCO: UnionInteractionsByHcp | null) => void;
  getTableState: (tableId: string) => TableState;
  setTableOffset: (tableId: string, offset: number) => void;
  setTableLimit: (tableId: string, limit: number) => void;
  setTableSorts: (tableId: string, sorts: SortConfig[]) => void;
}

export const useChannelFocusStore = create<ChannelFocusState & ChannelFocusActions>((set, get) => {
  const computeDefaultFilters = () => getDefaultFiltersState(get().filterKeys);

  const getDefaultTableState = (): TableState => ({
    pagination: { offset: 0, limit: 10 },
    sorts: [{ field: 'numberInteractions', desc: true }],
  });

  // Persist the secondary segment filter per channel so selections survive
  // navigation and tab switches. The default state (empty) is omitted from
  // storage to keep parity with other local filters.
  const persistInteractionFilter = (channel: Channel, filter: FilterState) => {
    const stored = readSecondarySegmentFilters();
    const defaultFilters = computeDefaultFilters();
    if (_isEqual(filter, defaultFilters)) {
      const { [channel]: _removed, ...rest } = stored;
      writeSecondarySegmentFilters(rest);
      return;
    }
    writeSecondarySegmentFilters({ ...stored, [channel]: filter });
  };

  const initialChannel = Channel.VISITS_1_TO_1;

  return {
    channel: initialChannel,
    filterKeys: [],
    interactionFilter: getSecondarySegmentFiltersForChannel(initialChannel),
    timeRangeInMonth: 24,
    mode: 'hcp',
    currentHCO: null,
    tables: {},
    setChannel: (channel: Channel) =>
      set({ channel, interactionFilter: getSecondarySegmentFiltersForChannel(channel) }),
    setInteractionFilter: (filter: FilterState) => {
      persistInteractionFilter(get().channel, filter);
      set({ interactionFilter: filter });
    },
    clearFilters: () => {
      const defaultFilters = computeDefaultFilters();
      persistInteractionFilter(get().channel, defaultFilters);
      set({ interactionFilter: defaultFilters });
    },
    setMode: (mode: Mode) => set({ mode }),
    clearSingleFilter: (filterKey: FilterType, value: FilterValue) => {
      const previousFilterState = get().interactionFilter;
      const previousSelectedFilterValue = previousFilterState[filterKey];
      const defaultFilters = computeDefaultFilters();
      const newFilter = {
        ...previousFilterState,
        [filterKey]: isArray(previousSelectedFilterValue)
          ? previousSelectedFilterValue.filter(filterValue => filterValue !== value)
          : defaultFilters[filterKey],
      };
      const cleaned = cleanFilterState(newFilter);
      persistInteractionFilter(get().channel, cleaned);
      set({ interactionFilter: cleaned });
    },
    getActiveFilters: () => {
      const defaultFilters = computeDefaultFilters();
      return getActiveFilters(get().interactionFilter, defaultFilters);
    },
    isFilterActive: () => {
      const defaultFilters = computeDefaultFilters();
      return !_isEqual(get().interactionFilter, defaultFilters);
    },
    setTimeRangeInMonth: (timeRangeInMonth: number) => set({ timeRangeInMonth }),
    setFilterKeys: (keys: FilterType[]) =>
      set({
        filterKeys: keys,
        interactionFilter: getSecondarySegmentFiltersForChannel(get().channel),
      }),
    setCurrentHCO: (currentHCO: UnionInteractionsByHcp | null) => set({ currentHCO }),
    getTableState: (tableId: string) => {
      const tables = get().tables;
      return tables[tableId] ?? getDefaultTableState();
    },
    setTableOffset: (tableId: string, newOffset: number) => {
      const currentState = get().getTableState(tableId);
      if (newOffset !== currentState.pagination.offset) {
        set({
          tables: {
            ...get().tables,
            [tableId]: {
              ...currentState,
              pagination: { ...currentState.pagination, offset: newOffset },
            },
          },
        });
      }
    },
    setTableLimit: (tableId: string, newLimit: number) => {
      const currentState = get().getTableState(tableId);
      if (newLimit !== currentState.pagination.limit) {
        set({
          tables: {
            ...get().tables,
            [tableId]: {
              ...currentState,
              pagination: { limit: newLimit, offset: 0 },
            },
          },
        });
      }
    },
    setTableSorts: (tableId: string, newSorts: SortConfig[]) => {
      const currentState = get().getTableState(tableId);
      if (!newSorts.length || _isEqual(newSorts, currentState.sorts)) return;

      set({
        tables: {
          ...get().tables,
          [tableId]: {
            ...currentState,
            sorts: newSorts,
          },
        },
      });
    },
  };
});

// Sync the live store when secondary segment filters are restored externally
// (saved filter sets, shared views) or cleared. Those flows write to
// sessionStorage and dispatch SESSION_STORAGE_SYNC_EVENT. Events originating
// from the store's own writes carry source:'hook' and are ignored to avoid
// feedback loops.
if (typeof window !== 'undefined') {
  window.addEventListener(SESSION_STORAGE_SYNC_EVENT, event => {
    if (!(event instanceof CustomEvent)) return;
    const { key, source } = event.detail ?? {};
    if (key !== SessionStorageKey.CHANNEL_FOCUS_SECONDARY_SEGMENT_FILTERS || source === 'hook') return;
    const { channel, interactionFilter } = useChannelFocusStore.getState();
    const restored = getSecondarySegmentFiltersForChannel(channel);
    if (!_isEqual(restored, interactionFilter)) {
      useChannelFocusStore.setState({ interactionFilter: restored });
    }
  });
}
